import React, { useEffect } from "react";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import { 
  Typography, 
  Card, 
  Space, 
  Tag, 
  Badge, 
  Row, 
  Col, 
  Spin, 
  Result,
  Breadcrumb,
  Divider,
  Modal,
  Dropdown,
  Menu,
  Button,
} from "antd";
import { 
  MoreOutlined, 
  LinkOutlined, 
  DatabaseOutlined,
  DownloadOutlined,
  GlobalOutlined
} from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroll-component";
import { it } from "date-fns/locale";
import LogoAnnunciFunebri from "../../images/logos/logo_impresa_cropped.png";
import formatDistance from "date-fns/formatDistance";
import { useAnnunciFunebriData } from "../../hooks/useAnnunciFunebriData";
import EdgeResizer from "../common/utils/EdgeResizer";

const { Title } = Typography;

const AnnuncioCard = ({
  objectId,
  isProd,
  title,
  url,
  photoUrl,
  createdAt,
  annuncioUrl,
  linkCliente,
  orgName,
  showOrg = false
}) => {
  const [previewVisible, setPreviewVisible] = React.useState(false);
  
  // Get optimized image URL through EdgeResizer to scale width to 300px
  const getOptimizedImageUrl = (url) => {
    if (!url) return LogoAnnunciFunebri;
    try {
      const env = isProd ? "prod" : "dev";
      const edgeResizer = new EdgeResizer(config.files[env], url);
      return edgeResizer.scaleToWidth(300).toString();
    } catch (error) {
      console.error('Error processing image URL:', error);
      return url; // Fallback to original URL
    }
  };
  
  
  const handleDownloadImage = () => {
    if (photoUrl) {
      const link = document.createElement('a');
      link.href = photoUrl;
      link.download = `annuncio_${objectId}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  return (
    <>
      <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 16 }}>
        <Card
          title={title || "Annuncio"}
          extra={
            <Space>
              <Dropdown 
                overlay={
                  <Menu>
                    {linkCliente && (
                      <Menu.Item 
                        key="open-annuncio-cliente" 
                        icon={<GlobalOutlined />}
                        onClick={() => window.open(linkCliente, '_blank')}
                      >
                        Apri su sito cliente
                      </Menu.Item>
                    )}
                    {annuncioUrl && (
                      <Menu.Item 
                        key="open-annuncio-af" 
                        icon={<GlobalOutlined />}
                        onClick={() => window.open(annuncioUrl, '_blank')}
                      >
                        Apri su annuncifunebri.it
                      </Menu.Item>
                    )}
                    {photoUrl && (
                      <Menu.Item 
                        key="download-photo" 
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadImage}
                      >
                        Scarica foto
                      </Menu.Item>
                    )}
                    {showOrg && url !== '#' && (
                      <>
                        <Menu.Divider />
                        <Menu.Item 
                          key="org-details" 
                          icon={<LinkOutlined />}
                          onClick={() => window.open(url, '_blank')}
                        >
                          Dettagli organizzazione
                        </Menu.Item>
                      </>
                    )}
                    <Menu.Divider />
                    <Menu.Item 
                      key="parse-dashboard" 
                      icon={<DatabaseOutlined />}
                      onClick={() => window.open(`${config.parseDashboard[isProd ? "prod" : "dev"]}/browser/annunci_funebri?filters=[{"field":"objectId","constraint":"eq","compareTo":"${objectId}"}]`, '_blank')}
                    >
                      Apri in Parse Dashboard
                    </Menu.Item>
                  </Menu>
                } 
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          }
          hoverable
          style={{ height: '100%' }}
          bodyStyle={{ padding: 12 }}
        >
          <div 
            style={{ marginBottom: 12, cursor: photoUrl ? 'pointer' : 'default' }}
            onClick={() => photoUrl && setPreviewVisible(true)}
          >
            <img 
              src={getOptimizedImageUrl(photoUrl)} 
              width="100%" 
              style={{ 
                height: 200, 
                objectFit: 'contain', 
                backgroundColor: '#f5f5f5' 
              }} 
              alt="Foto annuncio" 
            />
          </div>
          <div style={{ 
            marginTop: "10px",
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <Tag style={{ width: 'fit-content' }}>
              {formatDistance(new Date(createdAt), new Date(), { locale: it })}
            </Tag>
            {showOrg && orgName && (
              <Tag 
                color="blue" 
                style={{ 
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={orgName}
              >
                {orgName}
              </Tag>
            )}
          </div>
        </Card>
      </Col>
      
      {photoUrl && (
        <Modal
          open={previewVisible}
          onCancel={() => setPreviewVisible(false)}
          footer={null}
          width="90vw"
          style={{ top: 20 }}
          bodyStyle={{ padding: 20, textAlign: 'center' }}
        >
          <img 
            src={photoUrl} 
            alt="Foto annuncio preview" 
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </Modal>
      )}
    </>
  );
};

/**
 * Consolidated Annunci Funebri component
 * @param {string} orgId - Optional organization ID. If not provided, shows all organizations
 * @param {boolean} showBreadcrumb - Whether to show breadcrumb navigation
 * @param {boolean} showTitle - Whether to show the main title
 */
const AnnunciFunebri = ({ 
  orgId = null, 
  showBreadcrumb = true,
  showTitle = true
}) => {
  const { isProd } = useAppContext();
  const isGlobalView = !orgId;
  const { loading, initialFetchDone, items, count, hasMore, fetchInitialData, loadMore } = useAnnunciFunebriData(orgId);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Show loading spinner only on initial load
  if (!initialFetchDone && loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Only show "no data" message after initial fetch is complete
  if (count === 0 && initialFetchDone && !loading) {
    return (
      <Result
        status="info"
        title="Nessun annuncio funebre trovato"
        subTitle={isGlobalView 
          ? "Non ci sono annunci funebri pubblicati nel sistema."
          : "Non ci sono annunci funebri pubblicati per questa organizzazione."
        }
      />
    );
  }

  return (
    <div style={{ margin: isGlobalView ? "16px 16px" : "0" }}>
      {showBreadcrumb && isGlobalView && (
        <>
          <Breadcrumb style={{ marginBottom: "20px" }}>
            <Breadcrumb.Item>Giotto</Breadcrumb.Item>
            <Breadcrumb.Item>Annunci Funebri</Breadcrumb.Item>
          </Breadcrumb>
          <Divider />
        </>
      )}
      
      {showTitle && (
        <>
          <Title level={3} style={{ marginBottom: "40px" }}>
            <Space>
              Annunci Funebri
              <Badge overflowCount={5000} count={count} showZero />
            </Space>
          </Title>
          <Divider />
        </>
      )}
      
      <InfiniteScroll
        dataLength={items.length}
        next={loadMore}
        hasMore={hasMore}
        loader={<Spin size="large" style={{ display: 'block', textAlign: 'center', margin: '20px 0' }} />}
        endMessage={
          items.length > 0 && (
            <div style={{ textAlign: 'center', margin: '20px 0', color: '#999' }}>
              Non ci sono altri annunci da caricare
            </div>
          )
        }
      >
        <Row gutter={[16, 16]}>
          {items.map((attachment, index) => {
            const url = attachment.org_pointer?.objectId 
              ? `/orgs/${attachment.org_pointer.objectId}`
              : '#';
            const photo = attachment.foto?.url;
            const orgName = attachment.org_pointer?.nome;
            
            return (
              <AnnuncioCard
                key={attachment.objectId || index}
                objectId={attachment.objectId}
                createdAt={attachment.createdAt}
                isProd={isProd}
                org={attachment.org_pointer}
                title=""
                url={url}
                photoUrl={photo}
                annuncioUrl={attachment.link_annuncifunebri}
                linkCliente={attachment.link_cliente}
                orgName={orgName}
                showOrg={isGlobalView}
              />
            );
          })}
        </Row>
      </InfiniteScroll>
    </div>
  );
};

export default AnnunciFunebri;